import { CSSProperties, useContext, useEffect, useRef, useState } from 'react';
import { GET_PLAYER_PROMOS } from 'constants/eventConstants';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import gsap from 'gsap';
import { useAppSelector } from 'hooks';
import { useTableSelector } from 'hooks/useTableSelector';
import { Promotion } from 'reducers/playerPromos';

import { buildPromoDisplay, getCurrentWeekEndDateLabel } from './promoDisplay';
import { getPromoGameIndicatorCopy } from './promoMessaging';
import type { PromoVisualTheme } from './promoTheme';
import { getPromoVisualTheme, getTierProgressFill } from './promoTheme';

type GamePromoProgressIndicatorProps = {
  collapsedOnly?: boolean;
  cashTablesOnly?: boolean;
};

const GamePromoProgressIndicator = ({
  collapsedOnly = false,
  cashTablesOnly = false
}: GamePromoProgressIndicatorProps) => {
  const { socket } = useContext(SocketContext);
  const promotions = useAppSelector((state) => state.playerPromos.promotions);
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  const game = useTableSelector((table) => table.game);
  const fillRef = useRef<HTMLSpanElement>(null);
  const cardRef = useRef<HTMLButtonElement>(null);
  const burstRef = useRef<HTMLDivElement>(null);
  const goalCelebratedRef = useRef<string | number | null>(null);
  const requestedPromosRef = useRef(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const activePromo = promotions.find((promo: Promotion) => promo.is_active);
  const display = activePromo ? buildPromoDisplay(activePromo) : null;
  const isCashTable = isCashGameTable(game);
  const tableQualifies = activePromo ? tableQualifiesForPromo(game, activePromo) : false;
  const theme = getPromoVisualTheme(
    display?.format || 'legacy',
    activePromo?.player_facing_config?.theme
  );
  const isParchmentTheme = theme.detailsCardFrameStyle !== undefined;
  const pillTheme = getTablePromoPillTheme(theme);
  const remaining = display ? Math.max(display.targetValue - display.progressValue, 0) : 0;
  const remainingLabel =
    display?.hybridDetails?.remainingLabel ||
    display?.remainingLabel ||
    (display?.format === 'weekly_timeback_credit'
      ? `$${remaining.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
      : `${Number.isInteger(remaining) ? remaining.toFixed(0) : remaining.toFixed(1)}h`);
  const inlineCopy =
    activePromo && display
      ? getPromoGameIndicatorCopy({
          promoName: activePromo.promo_name,
          remainingLabel,
          display,
          playerFacingConfig: activePromo.player_facing_config
        })
      : null;
  const reachedGoal = Boolean(
    display && display.targetValue > 0 && display.progressValue >= display.targetValue
  );
  const canExpand = !collapsedOnly;
  const expanded = canExpand && isExpanded;
  const promoKey = activePromo?.promo_id || activePromo?.promo_name || null;
  const goalAnimationKey = activePromo
    ? `${promoKey}:${(activePromo.hours as any)?.current_week || ''}:${display?.targetValue || 0}`
    : null;

  useEffect(() => {
    if (promotions.length > 0 || requestedPromosRef.current || !socket) return;

    const fallbackPlayerGUID = localStorage.getItem('playerGUID') || '';
    const resolvedPlayerGUID = playerGUID || fallbackPlayerGUID;
    if (!resolvedPlayerGUID) return;

    requestedPromosRef.current = true;
    socket.emit(GET_PLAYER_PROMOS, { playerGUID: resolvedPlayerGUID });
  }, [playerGUID, promotions.length, socket]);

  useEffect(() => {
    if (!fillRef.current || !display) return;

    gsap.to(fillRef.current, {
      width: `${display.progressPct}%`,
      height: '100%',
      duration: 0.55,
      ease: 'power2.out'
    });
  }, [display?.progressPct]);

  useEffect(() => {
    if (!reachedGoal || !cardRef.current || goalCelebratedRef.current === goalAnimationKey) return;

    goalCelebratedRef.current = goalAnimationKey;
    const particles = burstRef.current?.querySelectorAll('.game-promo-burst-particle') || [];
    const timeline = gsap.timeline();
    timeline
      .to(cardRef.current, {
        scale: 1.035,
        boxShadow: `0 0 0 1px ${theme.accentMuted}, 0 0 34px ${theme.accentColor}`,
        duration: 0.22,
        ease: 'power2.out'
      })
      .fromTo(
        particles,
        { x: 0, y: 0, opacity: 0, scale: 0.25 },
        {
          x: (index) => burstVectors[index % burstVectors.length].x,
          y: (index) => burstVectors[index % burstVectors.length].y,
          opacity: 1,
          scale: (index) => (index % 3 === 0 ? 1.15 : 0.85),
          duration: 0.34,
          stagger: 0.015,
          ease: 'power2.out'
        },
        '<'
      )
      .to(
        particles,
        {
          opacity: 0,
          scale: 0.2,
          duration: 0.34,
          stagger: 0.01,
          ease: 'power2.in'
        },
        '>-0.08'
      )
      .to(cardRef.current, {
        scale: 1,
        boxShadow: expanded ? theme.boxShadow : 'none',
        duration: 0.42,
        ease: 'elastic.out(1, 0.55)'
      });

    return () => {
      timeline.kill();
    };
  }, [
    goalAnimationKey,
    expanded,
    reachedGoal,
    theme.accentColor,
    theme.accentMuted,
    theme.boxShadow
  ]);

  if (!activePromo || !display || !game) return null;
  if (cashTablesOnly && !isCashTable) return null;

  const promoBadgeLabel =
    inlineCopy?.promoBadgeLabel ||
    display.earnedLabel ||
    display.formatLabel ||
    activePromo.promo_name;
  const promoAriaLabel =
    promoBadgeLabel.trim().toLowerCase() === 'promo'
      ? 'Promo progress'
      : `${promoBadgeLabel} promo progress`;
  const metaLabel =
    inlineCopy?.statusLabel ||
    (display.format === 'weekly_timeback_credit' ? 'To Go' : reachedGoal ? 'Done' : 'Left');
  const endLabel = getTablePromoEndLabel(activePromo);
  // Two-tier formats (EN-11291): the fill runs green to gold toward the top tier and turns gold
  // metal once it is earned; the earned amount then reads in the $100K gold.
  const isTopTier = display.tierState === 'bonusAchieved';
  const pillFill = getTierProgressFill(theme, display.tierState);
  const pillFillText = isTopTier ? GOLD_INK : pillTheme.fillText;

  return (
    <button
      type='button'
      ref={cardRef}
      className={`game-promo-indicator${expanded ? ' is-expanded' : ' is-collapsed'}${
        isParchmentTheme ? ' is-parchment' : ' is-themed'
      }${collapsedOnly ? ' is-grid-pill' : ''}`}
      aria-expanded={expanded}
      aria-label={promoAriaLabel}
      onClick={canExpand ? () => setIsExpanded((current) => !current) : undefined}
      style={{
        ...containerStyle,
        background: theme.background,
        borderColor: theme.borderColor,
        borderRadius: expanded ? (isParchmentTheme ? 6 : 8) : 999,
        boxShadow: expanded ? theme.boxShadow : 'none',
        color: theme.iconColor,
        ['--game-promo-glow' as any]: theme.accentMuted
      }}
    >
      <style>{gamePromoAnimationCss}</style>
      {expanded && theme.detailsCardFrameStyle && (
        <span aria-hidden='true' className='game-promo-frame' style={theme.detailsCardFrameStyle} />
      )}
      <div ref={burstRef} style={burstLayerStyle} aria-hidden='true'>
        {burstVectors.map((_, index) => (
          <span
            key={index}
            className='game-promo-burst-particle'
            style={{
              ...burstParticleStyle,
              background: index % 2 === 0 ? theme.iconColor : theme.accentColor,
              boxShadow: `0 0 8px ${theme.accentColor}`
            }}
          />
        ))}
      </div>

      <div
        className='game-promo-gold'
        style={{
          ...goldPillStyle,
          background: pillTheme.track,
          borderColor: pillTheme.border,
          ['--game-promo-fill' as any]: `${display.progressPct}%`
        }}
      >
        <span
          ref={fillRef}
          className='game-promo-fill'
          style={{
            ...fillStyle,
            background: pillFill
          }}
        />
        <span
          className='game-promo-gold-label'
          style={{ ...goldLabelStyle, ...pillTheme.badgeTextStyle, color: pillTheme.trackText }}
        >
          {promoBadgeLabel}
        </span>
        <span
          className='game-promo-gold-label-over'
          style={{ ...goldLabelOverStyle, ...pillTheme.badgeTextStyle, color: pillFillText }}
          aria-hidden='true'
        >
          {promoBadgeLabel}
        </span>
      </div>

      {expanded && (
        <div className='game-promo-expanded' style={expandedContentStyle}>
          <div style={expandedProgressStyle}>
            <span
              style={{
                ...expandedProgressValueStyle,
                ...pillTheme.valueTextStyle,
                color: isTopTier ? GOLD_TEXT : pillTheme.expandedText
              }}
            >
              {remainingLabel}
            </span>
            <span
              style={{
                ...expandedProgressLabelStyle,
                ...pillTheme.metaTextStyle,
                color: pillTheme.expandedText
              }}
            >
              {metaLabel}
            </span>
          </div>
          <div
            style={{
              ...expandedEndStyle,
              ...pillTheme.detailTextStyle,
              borderTopColor: pillTheme.expandedDivider,
              color: pillTheme.expandedText
            }}
          >
            {endLabel}
          </div>
          {!tableQualifies && (
            <div
              style={{
                ...expandedWarningStyle,
                ...pillTheme.detailTextStyle,
                color: pillTheme.expandedText
              }}
            >
              Eligible tables only
            </div>
          )}
        </div>
      )}
    </button>
  );
};

export default GamePromoProgressIndicator;

const GOLD_TEXT = '#ffd97a';
const GOLD_INK = '#3b2008';

const burstVectors = [
  { x: -92, y: -30 },
  { x: -70, y: 18 },
  { x: -46, y: -42 },
  { x: -16, y: 34 },
  { x: 20, y: -48 },
  { x: 46, y: 32 },
  { x: 74, y: -22 },
  { x: 94, y: 12 },
  { x: -26, y: -18 },
  { x: 28, y: 18 }
];

const isCashGameTable = (game: any): boolean => game?.tabletype === 's';

const tableQualifiesForPromo = (game: any, promo: Promotion): boolean => {
  if (!isCashGameTable(game)) return false;

  const rules = normalizeRules(promo.additional_rules);
  const minSmallBlind = toNumber(
    rules.min_sb ??
      rules.minSmallBlind ??
      rules.MinSmallBlind ??
      rules.minimum_small_blind ??
      rules.minimumSmallBlind
  );
  const tableSmallBlind = getTableSmallBlind(game);
  const allowedGameTypes = Array.isArray(rules.game_types)
    ? rules.game_types.map((gameType: string) => gameType.toLowerCase())
    : [];
  const tableGameType = normalizeTableGameType(game);

  if (tableSmallBlind < minSmallBlind) return false;
  if (allowedGameTypes.length === 0) return true;

  return allowedGameTypes.includes(tableGameType);
};

const normalizeRules = (rules: Promotion['additional_rules']): any => {
  if (!rules) return {};
  if (typeof rules === 'object') return rules;

  try {
    return JSON.parse(rules);
  } catch {
    return {};
  }
};

const normalizeTableGameType = (game: any): 'holdem' | 'omaha' => {
  const tableGame = String(game?.game || game?.gameType || '').toLowerCase();
  return tableGame.includes('omaha') ? 'omaha' : 'holdem';
};

const getTableSmallBlind = (game: any): number => {
  const blindLevelMatch = String(
    game?.blindLevels || game?.blindLevel || game?.limits || game?.stakes || game?.title || ''
  ).match(/\$?\s*([\d.]+)\s*\/\s*\$?\s*[\d.]+/);
  if (blindLevelMatch) return toNumber(blindLevelMatch[1]);

  const numericCandidates = [
    game?.sb,
    game?.currentSB,
    game?.tableSmallBlind,
    game?.smallBlind,
    game?.small_blind,
    game?.sBlind
  ];

  return numericCandidates.map(toNumber).find((value) => value > 0) || 0;
};

const toNumber = (value: any): number => {
  const normalized =
    typeof value === 'string' ? value.replace(/[$,\s]/g, '').match(/-?\d+(\.\d+)?/)?.[0] : value;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : 0;
};

const getTablePromoEndLabel = (promo: Promotion): string => {
  const label = getCurrentWeekEndDateLabel(promo, { padded: true });
  return label ? `Week Ends ${label}` : 'Promo Active';
};

const getTablePromoPillTheme = (theme: PromoVisualTheme) => {
  const isParchment = theme.detailsCardFrameStyle !== undefined;

  const badgeTextStyle: CSSProperties = isParchment
    ? {
        fontFamily: "'Rye', serif",
        fontSize: 15,
        fontWeight: 400,
        letterSpacing: '0.02em',
        textTransform: 'none'
      }
    : {
        fontFamily: "'Oswald', sans-serif",
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: '0.08em',
        textTransform: 'uppercase'
      };
  const valueTextStyle: CSSProperties = isParchment
    ? {
        fontFamily: "'Rye', serif",
        fontSize: 13,
        fontWeight: 400,
        letterSpacing: '0.01em'
      }
    : {
        fontFamily: "'Oswald', sans-serif",
        fontSize: 13,
        fontWeight: 800,
        letterSpacing: '0.04em'
      };
  const metaTextStyle: CSSProperties = isParchment
    ? {
        fontFamily: "'Special Elite', monospace",
        fontSize: 8.5,
        fontWeight: 400,
        letterSpacing: '0.1em'
      }
    : {
        fontFamily: "'Oswald', sans-serif",
        fontSize: 8,
        fontWeight: 800,
        letterSpacing: '0.08em'
      };
  const detailTextStyle: CSSProperties = isParchment
    ? {
        fontFamily: "'Special Elite', monospace",
        letterSpacing: '0.13em'
      }
    : {
        fontFamily: "'Oswald', sans-serif",
        fontWeight: 700,
        letterSpacing: '0.07em'
      };

  return {
    track: theme.trackColor,
    border: theme.borderColor,
    fill: theme.fill,
    trackText: theme.iconColor,
    fillText: theme.onFillColor,
    expandedText: theme.iconColor,
    expandedDivider: theme.accentMuted,
    badgeTextStyle,
    valueTextStyle,
    metaTextStyle,
    detailTextStyle
  };
};

const gamePromoAnimationCss = `
  @import url('https://fonts.googleapis.com/css2?family=Rye&family=Special+Elite&family=Oswald:wght@400;500;600;700;800&display=swap');

  .game-promo-indicator {
    width: auto;
    min-width: 0;
    padding: 4px;
  }

  .game-promo-indicator.is-parchment.is-expanded::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.14 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    mix-blend-mode: multiply;
    opacity: 0.35;
    pointer-events: none;
    z-index: 0;
  }

  .game-promo-indicator.is-collapsed {
    background: transparent !important;
    border-color: transparent !important;
    border-radius: 999px;
    box-shadow: none !important;
    overflow: visible;
  }

  .game-promo-indicator.is-collapsed::after {
    display: none;
  }

  .game-promo-indicator.is-themed::after {
    display: none;
  }

  .game-promo-indicator.is-expanded {
    width: 124px;
  }

  .game-promo-indicator.is-parchment.is-expanded {
    width: 118px;
    padding: 8px;
  }

  .game-promo-indicator.is-themed.is-expanded {
    border-radius: 8px;
    padding: 8px;
  }

  .game-promo-indicator.is-collapsed .game-promo-gold {
    width: auto;
    border-radius: 999px;
  }

  .game-promo-indicator.is-collapsed .game-promo-fill::after {
    display: none;
  }

  /* PERF: box-shadow cannot be GPU-composited, so animating it repainted this
     element (plus its 30px glow spread) every frame, forever. Measured on a
     Galaxy S24+: the promo's animations drove ~36 layouts/sec and ~45% of all
     style-recalc time on an idle table. The static shadow below is the old
     keyframe's rest state; the BRIGHT half moved to ::after and pulses via
     opacity only, which is composited. Same look, no per-frame repaint.
     See docs/development/game-client-performance.md ("pulsing glow"). */
  .game-promo-gold {
    /* PERF: no animation. Held at the old keyframe's BRIGHT state. An always-on
       animation keeps the frame pipeline running at the refresh rate for the
       whole session; an opacity-pulsed layer was tried first but it adds a
       composited layer, and a stepped variant was rejected on review for looking
       like flickering. See docs/development/game-client-performance.md. */
    box-shadow:
      inset 0 1px 2px rgba(0,0,0,0.5),
      0 2px 8px rgba(0,0,0,0.5),
      0 0 18px var(--game-promo-glow),
      0 0 30px var(--game-promo-glow);
  }


  .game-promo-fill::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(100deg, transparent 0%, rgba(255,255,255,0.34) 16%, transparent 34%);
    background-size: 230% 100%;
    mix-blend-mode: screen;
    pointer-events: none;
    /* PERF: always-on loop removed. */
    animation: none;
  }

  .game-promo-fill::after {
    content: "";
    position: absolute;
    top: -3px;
    bottom: -3px;
    right: -6px;
    width: 12px;
    border-radius: 50%;
    background: radial-gradient(ellipse 50% 50% at 50% 50%, rgba(255,255,255,0.92), rgba(255,255,255,0.42) 52%, transparent 74%);
    filter: blur(0.4px);
    pointer-events: none;
    /* PERF: always-on loop removed. */
    animation: none;
  }

  .game-promo-gold::before {
    content: "";
    position: absolute;
    top: -20%;
    bottom: -20%;
    left: -40%;
    width: 32%;
    background: linear-gradient(105deg, transparent 28%, rgba(255,255,255,0) 36%, rgba(255,255,255,0.55) 50%, rgba(255,255,255,0) 64%, transparent 72%);
    filter: blur(2px);
    mix-blend-mode: screen;
    pointer-events: none;
    /* PERF: animating the 'left' property invalidated layout on every frame
       (measured ~30 layouts/sec from this one rule). Same travel expressed as
       translateX, which is composited and never touches layout. */
    /* PERF: same sweep, fired every 14.4s instead of 3.6s; parked in between. */
    animation: gamePromoSheen 14.4s linear infinite;
    will-change: transform, opacity;
    z-index: 4;
  }

  @media (prefers-reduced-motion: reduce) {
    .game-promo-gold,
    .game-promo-gold::before,
    .game-promo-gold::after,
    .game-promo-fill::before,
    .game-promo-fill::after,
    .game-promo-burst-particle {
      animation: none;
    }
  }




  @keyframes gamePromoSheen {
    0% { transform: translateX(0); opacity: 0; }
    2% { opacity: 1; }
    13.5% { transform: translateX(468.75%); opacity: 1; }
    /* Parked for the remaining ~85%: no value changes, no frames. */
    15%, 100% { transform: translateX(468.75%); opacity: 0; }
  }

  @media (max-width: 900px) {
    .game-promo-indicator {
      bottom: 64px !important;
      left: 10px !important;
      top: auto !important;
      right: auto !important;
    }
  }

  @media (max-width: 480px) {
    .game-promo-indicator {
      bottom: 50px !important;
      left: 8px !important;
    }
  }

  @media (max-height: 650px) and (orientation: landscape) {
    .game-promo-indicator {
      bottom: 56px !important;
      left: 12px !important;
      top: auto !important;
      right: auto !important;
      transform: none;
    }
  }
`;

const containerStyle: CSSProperties = {
  position: 'absolute',
  bottom: 64,
  left: 16,
  zIndex: 9,
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 6,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 5,
  overflow: 'hidden',
  cursor: 'pointer',
  pointerEvents: 'auto',
  textAlign: 'center',
  userSelect: 'none',
  transition: 'transform 0.12s ease, filter 0.12s ease, width 0.18s ease, padding 0.18s ease'
};

const goldPillStyle: CSSProperties = {
  position: 'relative',
  zIndex: 2,
  width: 'auto',
  minWidth: 82,
  height: 28,
  border: '1.25px solid rgba(20, 12, 6, 0.85)',
  borderRadius: 999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  boxShadow:
    'inset 0 1px 2px rgba(0,0,0,0.55), inset 0 -1px 0 rgba(255,255,255,0.08), 0 2px 5px rgba(0,0,0,0.32)'
};

const fillStyle: CSSProperties = {
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  zIndex: 0,
  width: 'var(--game-promo-fill)',
  borderRadius: '999px 0 0 999px',
  boxShadow: 'inset 0 1px 0 rgba(255,250,210,0.85), inset 0 -2px 3px rgba(108,68,12,0.42)',
  overflow: 'hidden',
  transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)'
};

const goldLabelStyle: CSSProperties = {
  position: 'relative',
  zIndex: 2,
  color: '#ffd97a',
  fontFamily: "'Rye', serif",
  fontSize: 15,
  letterSpacing: '0.02em',
  lineHeight: 1.45,
  padding: '0 16px',
  textShadow: '0 1px 1px rgba(0,0,0,0.55)',
  whiteSpace: 'nowrap'
};

const goldLabelOverStyle: CSSProperties = {
  ...goldLabelStyle,
  position: 'absolute',
  inset: 0,
  display: 'grid',
  placeItems: 'center',
  zIndex: 3,
  color: '#3b2008',
  padding: 0,
  textShadow: '0 1px 0 rgba(255,235,170,0.55)',
  pointerEvents: 'none',
  clipPath: 'inset(0 calc(100% - var(--game-promo-fill)) 0 0)'
};

const expandedContentStyle: CSSProperties = {
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 5,
  width: '100%'
};

const expandedProgressStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 4
};

const expandedProgressValueStyle: CSSProperties = {
  color: '#4a230a',
  fontFamily: "'Rye', serif",
  fontSize: 13,
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '0.01em',
  lineHeight: 1
};

const expandedProgressLabelStyle: CSSProperties = {
  color: '#4a230a',
  fontFamily: "'Special Elite', monospace",
  fontSize: 8.5,
  letterSpacing: '0.1em',
  textTransform: 'uppercase'
};

const expandedEndStyle: CSSProperties = {
  borderTop: '1px dashed rgba(74,35,10,0.4)',
  color: '#4a230a',
  fontFamily: "'Special Elite', monospace",
  fontSize: 10.5,
  letterSpacing: '0.13em',
  opacity: 0.9,
  paddingTop: 3,
  position: 'relative',
  textTransform: 'uppercase',
  width: '100%'
};

const expandedWarningStyle: CSSProperties = {
  color: '#4a230a',
  fontFamily: "'Special Elite', monospace",
  fontSize: 8.5,
  letterSpacing: '0.08em',
  opacity: 0.78,
  textTransform: 'uppercase'
};

const burstLayerStyle: CSSProperties = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: 0,
  height: 0,
  pointerEvents: 'none',
  zIndex: 4
};

const burstParticleStyle: CSSProperties = {
  position: 'absolute',
  width: 5,
  height: 5,
  borderRadius: 999,
  opacity: 0,
  transform: 'translate(-50%, -50%)'
};
